import { Task } from "@freeconvert/freeconvert-node/dist/types";
import { useDeviceImport } from "client/hooks/import/useDeviceImport";
import { useExternalImport } from "client/hooks/import/useExternalImport";
import { FileData } from "client/types/FileData";
import { HandleFile, UploadSettings } from "client/types/HandleFile";
import { capitalize } from "client/utils/capitalize";
import { getSocket } from "client/utils/getSocket";
import { formatBytes } from "client/utils/formatBytes";
import { getRandomId } from "client/utils/getRandomId";
import { useCreateImportTask } from "client/utils/useCreateImportTask";
import { DEFAULT_TOAST_SHOW_TIME } from "components/common/Toast/ToastRenderer";
import { useToast } from "components/common/Toast/useToast";
import { addImage, deleteImage, setImageState, setImporting } from "store/features/bulkResize/actions";
import { BulkResizeImage } from "store/features/bulkResize/types";
import { createImportTask } from "store/features/compress/extraReducers/createImportTask";
import { FileState, UploadType } from "store/features/imageUploader/types";
import { store, useAppDispatch, useAppSelector } from "store/store";
import { ImageExtension } from "store/types/imageExtension";
import { Size } from "store/types/previewImage";
import { Socket } from "socket.io-client";
import { translateFunc } from "client/utils/translateFunc";
import { BATCH_UPLOAD_NUMBER } from "client/hooks/import/useBatchImport";
import { useDailyQuota } from "@/modules/DailyQuota/hooks/useDailyQuota";
import { useValidateMaxFileSize } from "@/modules/DailyQuota/hooks/useValidateMaxFileSize";

let socket: Socket | null = null;
let __images: BulkResizeImage[] = [];
let queue: {
    image: BulkResizeImage;
    fileData: FileData;
    uploadType: UploadType;
    settings: UploadSettings[UploadType];
}[] = [];

export const useAddImages = (onSubmit?: () => void) => {
    const dispatch = useAppDispatch();
    const { toast, removeBatch } = useToast();
    const images = useAppSelector((state) => state.bulkResize.images);
    __images = images;
    const { validateMaxFileSize } = useValidateMaxFileSize();
    const {
        showMaxFileSizeErrorToast,
        getBatchLimit,
        showBatchLimitErrorToast,
        getRemainingQuotaSynchronously,
        getBatchLimitSynchronously,
        showQuotaLimitExceededDeletedImagesToast,
    } = useDailyQuota();

    const handleFile: HandleFile = async (files, uploadType, settings) => {
        if (!socket) {
            socket = await getSocket();
        }
        const batchLimit = await getBatchLimit();
        const batchSize = files.length + store.getState().bulkResize.images.length;

        if (uploadType === "device" && !(await validateMaxFileSize(files.map((file) => file.file.size)))) {
            showMaxFileSizeErrorToast();
            return;
        }

        if (batchSize > batchLimit) {
            showBatchLimitErrorToast();

            while (files.length + store.getState().bulkResize.images.length > batchLimit) {
                if (files.length) {
                    files.pop();
                } else if (store.getState().bulkResize.images.length) {
                    const img = store.getState().bulkResize.images[-1];
                    dispatch(deleteImage(img.id));
                }
            }
        }

        if (!files[0]) return;

        onSubmit && onSubmit();

        const importingFiles = store.getState().bulkResize.images.filter((image) => image.state === "importing");

        files.forEach((file, index) => {
            const image = createNewImage(file, uploadType, settings);
            if (BATCH_UPLOAD_NUMBER > importingFiles.length + index) {
                handleSingleFile(image, file, uploadType, settings);
            } else {
                queue.push({ image, fileData: file, settings, uploadType: uploadType });
            }
        });
    };

    const proceedNextImports = () => {
        const importingImages = store.getState().bulkResize.images.filter((image) => image.state === "importing");
        if (queue.length === 0 || BATCH_UPLOAD_NUMBER <= importingImages.length) return;
        const nextInQueue = queue.splice(0, BATCH_UPLOAD_NUMBER - importingImages.length);
        nextInQueue.forEach(({ image, fileData, uploadType, settings }) => {
            handleSingleFile(image, fileData, uploadType, settings);
        });
    };

    const createNewImage = <T extends UploadType>(
        fileData: FileData,
        _uploadType: T,
        _settings: UploadSettings[T],
    ): BulkResizeImage => {
        let { file, src, size } = fileData;

        const image: BulkResizeImage = {
            id: getRandomId(),
            original: {
                width: 0,
                height: 0,
                size: size ?? formatBytes(file?.size), // if size is passed then no need to calculate file size
                src,
            },
            initialDimensions: {
                width: 0,
                height: 0,
            },
            preview: {
                width: 0,
                height: 0,
                size: size ?? formatBytes(file?.size), // if size is passed then no need to calculate file size
                src,
            },
            title: file.name,
            format: file.name.split(".")[file.name.split(".").length - 1] as ImageExtension,
            createdAt: new Date(),
            state: "added",
            crop: {
                height: 0,
                width: 0,
                top: 0,
                left: 0,
                ratio: "freeform",
                cropBox: null,
                customRatioValues: {
                    x: 1,
                    y: 1,
                },
            },
            flip: {
                horizontal: false,
                vertical: false,
            },
            rotate: {
                rotation: 0,
                straighten: 0,
            },
            operations: [],
        };

        dispatch(addImage(image));
        return image;
    };

    const { handleCreateImportTask } = useCreateImportTask();

    const handleSingleFile = async <T extends UploadType>(
        image: BulkResizeImage,
        fileData: FileData,
        uploadType: UploadType,
        settings: UploadSettings[T],
    ) => {
        const batchLimit = getBatchLimitSynchronously();
        const remainingQuota = getRemainingQuotaSynchronously();
        const batchSize = store.getState().compress.images.length + 1;
        if (batchSize > remainingQuota) {
            showQuotaLimitExceededDeletedImagesToast();
            return;
        }
        if (batchSize > batchLimit) {
            showBatchLimitErrorToast();
            return;
        }
        handleCreateImportTask<BulkResizeImage>(
            image,
            fileData,
            uploadType,
            settings,
            createImportTask,
            handleCreateImportTaskSuccess,
        );
    };

    type MetaData = {
        image: BulkResizeImage;
    };

    const onStart = (importTask: Task, metaData: MetaData, progress?: FileState["progress"]) => {
        dispatch(
            setImporting({
                imageId: metaData.image.id,
                importing: {
                    progress: progress ?? 0,
                },
                importTask: importTask,
            }),
        );
    };

    const onProgress = (_task: Task, progress: number, _speed: Size, metadata: MetaData) => {
        dispatch(
            setImporting({
                imageId: metadata.image.id,
                importing: {
                    progress,
                },
                importTask: _task,
            }),
        );
    };

    const onComplete = async (task: Task, metaData: MetaData) => {
        const size = task.result?.size;
        if (!size || !(await validateMaxFileSize([size]))) {
            showMaxFileSizeErrorToast();
            dispatch(deleteImage(metaData.image.id));
            return;
        }
        dispatch(
            setImporting({
                imageId: metaData.image.id,
                importing: {
                    progress: 100,
                },
                importTask: task,
            }),
        );
        dispatch(
            setImageState({
                imageId: metaData.image.id,
                imageState: "imported",
            }),
        );
        proceedNextImports();
    };

    const onFailureText = capitalize(translateFunc("upload-failed"));
    const onFailure = (task: Task, metaData: MetaData) => {
        dispatch(deleteImage(metaData.image.id));
        toast({
            closeable: true,
            position: "top-right",
            status: "error",
            text: onFailureText,
            time: DEFAULT_TOAST_SHOW_TIME,
        });
        proceedNextImports();
    };

    const onDisconnectText = `${translateFunc("you-are-offline")} ${translateFunc("trying-to-reconnect")}`;
    const onDisconnect = () => {
        toast({
            closeable: true,
            position: "top-right",
            status: "error",
            batch: "disconnect",
            text: onDisconnectText,
        });
    };

    const onReconnectText = translateFunc("online-again-toast");
    const onReconnect = () => {
        toast({
            closeable: true,
            position: "top-right",
            status: "success",
            batch: "reconnect",
            text: onReconnectText,
            time: DEFAULT_TOAST_SHOW_TIME,
        });
        removeBatch("disconnect");
    };

    const { handleDeviceImport } = useDeviceImport({
        onStart,
        onProgress,
        onComplete,
        onFailure,
        onDisconnect,
        onReconnect,
    });

    const { handleExternalImport } = useExternalImport({
        onStart,
        onComplete,
        onFailure,
        onDisconnect,
        onReconnect,
    });

    const handleCreateImportTaskSuccess = async (
        image: BulkResizeImage,
        importTask: Task,
        file: FileData,
        uploadType: UploadType,
    ) => {
        if (uploadType === "device") {
            handleDeviceImport(file, importTask, {
                image,
            } as MetaData);
        } else {
            handleExternalImport(importTask, {
                image,
            } as MetaData);
        }

        dispatch(
            setImageState({
                imageId: image.id,
                imageState: "importing",
            }),
        );
        dispatch(
            setImporting({
                imageId: image.id,
                importing: {
                    progress: "continuous",
                },
                importTask,
            }),
        );
    };

    return { handleFile };
};
